import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Flex,
  Heading,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
  Text,
  useDisclosure,
  CModal,
  ModalContent,
  ModalOverlay,
} from '@televet/kibble-ui';
import MergeProfilesStepper, { MergeStep } from './MergeProfilesStepper';
import MergeProfilesClientSearch from './MergeProfilesClientSearch';
import {
  ClinicEntityPhoneNumberUpdateManyWithWhereWithoutClinicPetParentInput,
  ClinicEntityPhoneNumberWhereUniqueInput,
  ClinicPetWhereUniqueInput,
  GetSidePanelPetParentProfileQuery,
  useMergePetParentProfilesMutation,
} from 'shared/types/graphql';
import { getClinicPetParentDisplayName } from 'shared/utils';
import MergeProfilesSelectInfo from './MergeProfilesSelectInfo';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { resetMergeProfilesResult } from 'shared/components/MergeProfiles/state/mergeProfilesSlice';
import MergeProfilesProfileCard from './MergeProfilesProfileCard';
import useClinicUser from 'shared/hooks/useClinicUser';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';
import { useSidePanel } from 'shared/components/SidePanel/state/providers/SidePanelProvider';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import UnsavedFormChangesModal, { PageType } from 'pages/Forms/components/UnsavedFormChangesModal';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import { Prompt, useHistory, useLocation } from 'react-router-dom';
import { Location } from 'history';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { useTracking } from 'shared/providers/TrackingProvider';
import { MergeProfileEvents } from 'shared/providers/TrackingProvider/EventNames';

interface IMergeProfilesModalProps {
  isOpen: boolean;
  onClose: () => void;
  clinicPetParent: GetSidePanelPetParentProfileQuery['clinicPetParent'];
  hasActiveConversation: boolean;
}

const MergeProfilesModal = ({
  isOpen: isMergeProfilesModalOpen,
  onClose: onMergeProfilesModalClose,
  clinicPetParent,
  hasActiveConversation,
}: IMergeProfilesModalProps): JSX.Element => {
  const [mergeStep, setMergeStep] = useState(MergeStep.Search);
  const [selectedMergeProfile, setSelectedMergeProfile] = useState<string | undefined>();

  const { toastMessagePop } = useCustomToast();
  const dispatch = useAppDispatch();
  const mergeProfilesResult = useAppSelector((state) => state.mergeProfiles.mergeProfileResult);
  const pimsProfile = useAppSelector((state) => state.mergeProfiles.pimsProfile);
  const secondProfileIsPrimary = useAppSelector((state) => state.mergeProfiles.secondProfileIsPrimary);

  const { viewClinicPetParent } = useSidePanelSearch();
  const { setIsOpen } = useSidePanel();

  const {
    isOpen: isUnsavedChangesOpen,
    onOpen: onUnsavedChangesOpen,
    onClose: onUnsavedChangesClose,
  } = useDisclosure();
  const location = useLocation();
  const [lastLocation, setLastLocation] = useState<Location>(location);
  const history = useHistory();
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const [shouldUnload, setShouldUnload] = useState(false);

  const [mergeProfiles, { loading: mergeInProgress }] = useMergePetParentProfilesMutation({
    refetchQueries: ['getSidePanelParentPets', 'getSidePanelPetParentProfile'],
  });

  const { currentClinicId } = useClinicUser();
  const { primaryIntegrationName } = useIntegrationsProvider();
  const { trackEvent } = useTracking();

  const disableNextButton = useMemo(() => !selectedMergeProfile, [selectedMergeProfile]);

  const submitButtonText = useMemo(() => {
    return mergeStep < 2 ? 'Next' : 'Complete Merge';
  }, [mergeStep]);

  const cancelButtonText = useMemo(() => {
    return mergeStep === 0 ? 'Cancel' : 'Back';
  }, [mergeStep]);

  const goToNextStep = useCallback(() => {
    trackEvent(mergeStep === 0 ? MergeProfileEvents.SearchProfilesStep : MergeProfileEvents.SelectInfoStep);
    setMergeStep((currentStep) => currentStep + 1);
  }, [mergeStep, trackEvent]);

  const goToPreviousStep = useCallback(() => {
    trackEvent(mergeStep === 2 ? MergeProfileEvents.BackToSelectInfoStep : MergeProfileEvents.BackToSearchProfileStep);

    if (mergeStep === 1) {
      setSelectedMergeProfile(undefined);
      dispatch(resetMergeProfilesResult());
    }

    setMergeStep((currentStep) => currentStep - 1);
  }, [trackEvent, mergeStep, dispatch]);

  const handleModalClose = useCallback(
    (submitting?: boolean) => {
      if (mergeStep > 0 && !submitting) {
        onUnsavedChangesOpen();
      } else {
        onMergeProfilesModalClose();
        dispatch(resetMergeProfilesResult());
      }
    },
    [dispatch, mergeStep, onMergeProfilesModalClose, onUnsavedChangesOpen],
  );

  const confirmModalClose = useCallback(() => {
    onMergeProfilesModalClose();
    dispatch(resetMergeProfilesResult());
  }, [dispatch, onMergeProfilesModalClose]);

  const completeMerge = useCallback(async () => {
    if (!currentClinicId) {
      return;
    }

    trackEvent(MergeProfileEvents.ConfirmMergedInfo);

    const [firstName, lastName] = (mergeProfilesResult.name || '').split(' ');
    const { email, sendSurvey, contactPreferences, phoneNumbers, pets } = mergeProfilesResult;

    const updatePhoneNumbersInput = phoneNumbers.map(
      (phoneNumber): ClinicEntityPhoneNumberUpdateManyWithWhereWithoutClinicPetParentInput => {
        return {
          where: {
            id: {
              equals: phoneNumber.id,
            },
          },
          data: {
            number: phoneNumber.number,
            isPrimary: phoneNumber.isPrimary,
            // if both profiles have the same number and user chooses to keep one but not the other, don't delete it
            isDeleted: !phoneNumbers
              .filter(({ number }) => number === phoneNumber.number)
              .some(({ isSelected }) => isSelected),
          },
        };
      },
    );

    // all numbers from both profiles are automatically connected, so just disconnect those that are not selected
    const disconnectPhoneNumbersInput = phoneNumbers
      .filter((phoneNumber) => !phoneNumber.isSelected)
      .map((phoneNumber): ClinicEntityPhoneNumberWhereUniqueInput => {
        return {
          id: phoneNumber.id,
        };
      });

    const connectPetsInput = pets.map((pet): ClinicPetWhereUniqueInput => {
      return {
        id: pet.id,
      };
    });

    const keepId = (secondProfileIsPrimary ? selectedMergeProfile : clinicPetParent?.id) || '';
    const deleteId = (secondProfileIsPrimary ? clinicPetParent?.id : selectedMergeProfile) || '';

    try {
      const mergeResultData = await mergeProfiles({
        variables: {
          data: {
            clinicId: currentClinicId,
            keepId,
            deleteId,
            update: {
              firstName,
              lastName,
              email,
              petParentSetting: {
                update: {
                  data: {
                    sendSurvey,
                    smsNotifications: !!contactPreferences?.sms && !contactPreferences.smsOptedOut,
                    emailNotifications: !!contactPreferences?.email && !contactPreferences.emailOptedOut,
                  },
                },
              },
              phoneNumbers: {
                updateMany: updatePhoneNumbersInput,
                disconnect: disconnectPhoneNumbersInput,
              },
              pets: {
                connect: connectPetsInput,
              },
            },
          },
        },
      });

      if (mergeResultData.data?.mergePetParent.success) {
        toastMessagePop(`Profiles merged successfully!`, STATUSES.success, 3000);
        dispatch(resetMergeProfilesResult());
        handleModalClose(true);

        // view kept profile in side panel
        setIsOpen(true);
        viewClinicPetParent({
          clinicPetParentId: keepId,
        });
      } else {
        toastMessagePop(`Error merging profiles! Please try again.`, STATUSES.error, 3000);
        console.warn(`Error merging profiles:`, mergeResultData.data?.mergePetParent.success);
      }
    } catch (e) {
      toastMessagePop(`Error merging profiles! Please try again.`, STATUSES.error, 3000);
    }
  }, [
    currentClinicId,
    trackEvent,
    mergeProfilesResult,
    secondProfileIsPrimary,
    selectedMergeProfile,
    clinicPetParent?.id,
    mergeProfiles,
    toastMessagePop,
    dispatch,
    handleModalClose,
    setIsOpen,
    viewClinicPetParent,
  ]);

  const handleSubmitButtonClicked = useCallback(async () => {
    if (mergeStep < 2) {
      goToNextStep();
    } else {
      await completeMerge();
    }
  }, [completeMerge, goToNextStep, mergeStep]);

  const handleCancelButtonClicked = useCallback(() => {
    if (mergeStep > 0) {
      goToPreviousStep();
    } else {
      trackEvent(MergeProfileEvents.CancelMergedInfo);
      handleModalClose();
    }
  }, [mergeStep, goToPreviousStep, trackEvent, handleModalClose]);

  const handleBlockedNavigation = (location: Location): boolean => {
    if (!confirmedNavigation && mergeStep > 0) {
      setLastLocation(location);
      setShouldUnload(true);
      if (mergeStep > 0) onUnsavedChangesOpen();
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      history.push(lastLocation?.pathname);
      setConfirmedNavigation(false);
    }
  }, [confirmedNavigation, lastLocation, history]);

  useEffect(() => {
    const unload = (event: BeforeUnloadEvent): void => {
      if (mergeStep > 0 && !shouldUnload) {
        // https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
        event.returnValue = ''; // deprecated-ish
        event.preventDefault(); // not fully supported yet
      }
    };

    window.addEventListener('beforeunload', unload);

    return (): void => window.removeEventListener('beforeunload', unload);
  }, [mergeStep, shouldUnload]);

  const clinicPetParentName = getClinicPetParentDisplayName({
    firstName: clinicPetParent?.firstName,
    lastName: clinicPetParent?.lastName,
  });

  const modalDescriptionComponent = useMemo(() => {
    if (mergeStep === 0) {
      return (
        <Text mb={6}>
          Search for profile to merge with
          <Text fontWeight="bold">{` ${clinicPetParentName}`}</Text>
        </Text>
      );
    } else if (mergeStep === 1) {
      return (
        <Text mb={6}>
          {`Select information to keep for merged profile. You can select multiple phone numbers, but only one profile
          name and one email to keep.${
            pimsProfile
              ? ` ${primaryIntegrationName} profiles cannot be updated, so certain properties may be locked.`
              : ''
          }`}
        </Text>
      );
    } else {
      return <Text mb={6}>Make sure all the information is correct. Merging cannot be undone!</Text>;
    }
  }, [clinicPetParentName, mergeStep, primaryIntegrationName, pimsProfile]);

  const modalBodyComponent = useMemo(() => {
    if (mergeStep === 0) {
      return (
        <MergeProfilesClientSearch
          clinicPetParentId={clinicPetParent?.id || ''}
          clinicPetParentPimsId={clinicPetParent?.pimsId || undefined}
          hasActiveConversation={hasActiveConversation}
          selectedMergeProfile={selectedMergeProfile}
          setSelectedMergeProfile={setSelectedMergeProfile}
        />
      );
    } else if (mergeStep === 1) {
      if (!selectedMergeProfile) {
        return (
          <Flex>
            <Text>No merge profile selected!</Text>
          </Flex>
        );
      } else {
        return (
          <MergeProfilesSelectInfo
            currentClientProfile={clinicPetParent}
            clinicId={currentClinicId}
            selectedMergeProfileId={selectedMergeProfile}
          />
        );
      }
    } else {
      return (
        <Flex justifyContent="center" w="850px">
          {mergeInProgress && (
            <Flex m="auto" position="absolute" mt="200px">
              <LoadingSpinner message="Merge in progress..." />
            </Flex>
          )}
          <MergeProfilesProfileCard
            cardTitle="Merged Profile"
            isMergeResult
            visibility={mergeInProgress ? 'hidden' : 'visible'}
          />
        </Flex>
      );
    }
  }, [clinicPetParent, hasActiveConversation, mergeInProgress, mergeStep, selectedMergeProfile, currentClinicId]);

  return (
    <>
      <CModal
        isOpen={isMergeProfilesModalOpen}
        onClose={(): void => handleModalClose(false)}
        size="5xl"
        isCentered={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <ModalCloseButton />
            <MergeProfilesStepper selectedStep={mergeStep} />
          </ModalHeader>
          <ModalBody mt={9} mb={6} alignSelf="center">
            <Flex direction="column">
              <Flex maxW="640px" direction="column" alignItems="center" alignSelf="center" textAlign="center">
                <Heading size="lg" mb={2}>
                  Merge Profiles
                </Heading>
                {modalDescriptionComponent}
              </Flex>
              {modalBodyComponent}
              <Flex mt={8} justifyContent="space-between">
                <Button variant="tertiary" onClick={handleCancelButtonClicked}>
                  {cancelButtonText}
                </Button>
                <Button
                  disabled={disableNextButton || mergeInProgress}
                  onClick={async (): Promise<void> => await handleSubmitButtonClicked()}
                  isLoading={mergeInProgress}
                >
                  {submitButtonText}
                </Button>
              </Flex>
            </Flex>
          </ModalBody>
        </ModalContent>
      </CModal>
      <Prompt when={mergeStep > 0} message={handleBlockedNavigation} />
      <UnsavedFormChangesModal
        pageType={PageType.Form}
        isOpen={isUnsavedChangesOpen}
        onClose={onUnsavedChangesClose}
        onConfirm={confirmModalClose}
      />
    </>
  );
};

export default MergeProfilesModal;
